import unescapedText from 'I18n/utils/unescapedText';
import Products from 'merchandise-lib/constants/Product';
import { currentOrderedPaidProductLevels } from '../constants/ProductLevel';
import { parseTierPrice } from '../price/tierPricing';
/*
 * The backend reports Integer.MAX_VALUE for a product with no purchase limit,
 * which is unusable as a stepper bound, so it collapses to a UI sanity cap.
 * Real limits pass through — Starter caps GTM seats at 10.
 */
export const MAX_STEPPER_QUANTITY = 1000;
const FSC_SKU_CATEGORIES = ['GTM_SEATS', 'OPS_SEATS', 'CORE_SEATS', 'FRONT_OFFICE_SEATS', 'HUBSPOT_CREDITS'];
const isFscSkuCategory = value => FSC_SKU_CATEGORIES.some(category => category === value);
const getFscSkuCategory = sku => {
  const category = sku === null || sku === void 0 ? void 0 : sku.skuCategoryName;
  return isFscSkuCategory(category) ? category : undefined;
};
export const getFscTierSeatApiNames = (catalog, tier) => {
  var _catalog$productsByTi, _tierOption$tierProdu;
  const tierOption = (_catalog$productsByTi = catalog.productsByTier) === null || _catalog$productsByTi === void 0 ? void 0 : _catalog$productsByTi[tier];
  if (!tierOption) {
    return null;
  }

  /* Credits are tier products too, but they're rendered by their own card and
   * added to the price catalog cart separately, so they're not a seat type. */
  const primaryApiName = tierOption.productApiName;
  const otherSeatApiNames = ((_tierOption$tierProdu = tierOption.tierProducts) !== null && _tierOption$tierProdu !== void 0 ? _tierOption$tierProdu : []).filter(apiName => {
    var _catalog$products;
    return apiName !== primaryApiName && getFscSkuCategory((_catalog$products = catalog.products) === null || _catalog$products === void 0 ? void 0 : _catalog$products[apiName]) !== 'HUBSPOT_CREDITS';
  });
  return {
    primaryApiName,
    otherSeatApiNames
  };
};
export const resolveFscMaxQuantity = sku => {
  var _sku$maxTouchlessPurc;
  const raw = (_sku$maxTouchlessPurc = sku === null || sku === void 0 ? void 0 : sku.maxTouchlessPurchasableQuantity) !== null && _sku$maxTouchlessPurc !== void 0 ? _sku$maxTouchlessPurc : sku === null || sku === void 0 ? void 0 : sku.maxAssistedPurchasableQuantity;
  if (raw === undefined) {
    return 0;
  }
  return Math.min(raw, MAX_STEPPER_QUANTITY);
};
export const getFscPreferredTermKey = (purchaseOptions, preferredTerm) => {
  if (preferredTerm && purchaseOptions[preferredTerm]) {
    return preferredTerm;
  }
  if (purchaseOptions.ANNUAL) {
    return 'ANNUAL';
  }
  if (purchaseOptions.MONTHLY) {
    return 'MONTHLY';
  }
  return undefined;
};
export const getFscPreferredPurchaseOption = (option, preferredTerm) => {
  const {
    purchaseOptions
  } = option.pricing;
  const termKey = getFscPreferredTermKey(purchaseOptions, preferredTerm);
  return termKey ? purchaseOptions[termKey] : undefined;
};

/* Tier options come back with an empty productPrices map, so per-seat prices
 * have to be read off a product plan option priced at one of each seat. */
export const buildFscSeatPrices = (option, preferredTerm) => {
  var _purchaseOption$produ;
  const purchaseOption = getFscPreferredPurchaseOption(option, preferredTerm);
  if (!purchaseOption) {
    return new Map();
  }
  /* `unitPrice` rather than `monthlyPrice`: the catalog cart also carries a
   * committed-credit line to surface a priced CUSTOM option, and the per-unit
   * price is correct regardless of what quantity that line was priced at. */
  return new Map(Object.entries((_purchaseOption$produ = purchaseOption.productPrices) !== null && _purchaseOption$produ !== void 0 ? _purchaseOption$produ : {}).map(([apiName, price]) => {
    var _parseTierPrice, _price$unitPrice;
    return [apiName, (_parseTierPrice = parseTierPrice((_price$unitPrice = price.unitPrice) !== null && _price$unitPrice !== void 0 ? _price$unitPrice : price.monthlyPrice)) !== null && _parseTierPrice !== void 0 ? _parseTierPrice : 0];
  }));
};

/*
 * Checkout-style row labels, e.g. "3 GTM Seats (Professional)" or "5,000
 * Additional HubSpot Credits". Each one already states its own quantity, taken
 * from the cart the option was priced with — so a label is the whole row text,
 * never a name to combine with a quantity, and a label for an owned quantity
 * has to come from an option priced at that quantity.
 */
export const buildFscProductDisplayNames = option => {
  var _option$productDispla;
  return new Map(Object.entries((_option$productDispla = option.productDisplayData) !== null && _option$productDispla !== void 0 ? _option$productDispla : {}).map(([apiName, displayData]) => [apiName, displayData.quantifiableDisplayName]));
};

/*
 * One of each seat in the tier, plus a single committed-credit unit: a cart
 * shaped purely to read unit prices back out of the product plan response,
 * independent of what the shopper selected.
 *
 * The committed-credit line is what makes the response include a priced CUSTOM
 * option. The backend drops that option entirely at zero units, so the shopper's
 * own cart can't be the source of the committed unit price.
 */
export const buildFscPriceCatalogCart = (catalog, tier) => {
  const seatApiNames = getFscTierSeatApiNames(catalog, tier);
  if (!seatApiNames) {
    return [];
  }
  return [seatApiNames.primaryApiName, ...seatApiNames.otherSeatApiNames, Products.HUBSPOT_CREDITS_COMMITTED].map(productApiName => ({
    productApiName,
    quantity: 1
  }));
};
export const getFscSkuDisplayName = (catalog, apiName) => {
  var _catalog$products$api, _catalog$products2;
  return (_catalog$products$api = (_catalog$products2 = catalog.products) === null || _catalog$products2 === void 0 || (_catalog$products2 = _catalog$products2[apiName]) === null || _catalog$products2 === void 0 ? void 0 : _catalog$products2.displayName) !== null && _catalog$products$api !== void 0 ? _catalog$products$api : apiName;
};
export const isFscOpsSeat = (catalog, apiName) => {
  var _catalog$products3;
  return getFscSkuCategory((_catalog$products3 = catalog.products) === null || _catalog$products3 === void 0 ? void 0 : _catalog$products3[apiName]) === 'OPS_SEATS';
};
export const partitionFscSeatTypes = allSeatTypes => ({
  seatTypes: allSeatTypes.filter(seat => seat.isPrimary || seat.stepper.quantity > 0),
  availableSeatTypes: allSeatTypes.filter(seat => !seat.isPrimary && seat.stepper.quantity === 0)
});

/* Seat names and descriptions aren't returned by the BE, so they're FE copy
 * keyed by the seat's category. The keys are written out per branch because
 * the i18n static analyzer only reads string literals at the call site — a
 * lookup table or a template literal resolves to a wildcard and fails build
 * validation. Add categories here as designs cover them.
 */
const getFscSeatName = skuCategory => {
  switch (skuCategory) {
    case 'GTM_SEATS':
      return unescapedText('fscConfigure.seatsCard.seatNames.GTM_SEATS');
    case 'OPS_SEATS':
      return unescapedText('fscConfigure.seatsCard.seatNames.OPS_SEATS');
    case 'CORE_SEATS':
      return unescapedText('fscConfigure.seatsCard.seatNames.CORE_SEATS');
    case 'FRONT_OFFICE_SEATS':
      return unescapedText('fscConfigure.seatsCard.seatNames.FRONT_OFFICE_SEATS');
    default:
      return undefined;
  }
};
const getFscSeatDescription = skuCategory => {
  switch (skuCategory) {
    case 'GTM_SEATS':
      return unescapedText('fscConfigure.seatsCard.seatDescriptions.GTM_SEATS');
    case 'OPS_SEATS':
      return unescapedText('fscConfigure.seatsCard.seatDescriptions.OPS_SEATS');
    default:
      return undefined;
  }
};
export const getFscSeatNameAndDescription = (catalog, apiName) => {
  var _catalog$products4, _ref, _getFscSeatName;
  const sku = (_catalog$products4 = catalog.products) === null || _catalog$products4 === void 0 ? void 0 : _catalog$products4[apiName];
  const skuCategory = getFscSkuCategory(sku);
  return {
    seatTypeId: apiName,
    name: (_ref = (_getFscSeatName = getFscSeatName(skuCategory)) !== null && _getFscSeatName !== void 0 ? _getFscSeatName : sku === null || sku === void 0 ? void 0 : sku.displayName) !== null && _ref !== void 0 ? _ref : apiName,
    description: getFscSeatDescription(skuCategory)
  };
};

/*
 * The backend's `maxTouchlessPurchasableQuantity` is net-of-owned (the Buying
 * service subtracts the portal's owned count before returning it). The stepper
 * however is absolute — its min/quantity are the owned floor, so the max must
 * be absolute too. Convert: absolute max = remaining + owned.
 *
 * Guard: when resolved is 0 (no BE limit data) keep it as 0 rather than
 * adding owned on top of a sentinel zero.
 */
export const resolveFscAbsoluteMaxQuantity = (sku, owned) => {
  const remaining = resolveFscMaxQuantity(sku);
  if (remaining <= 0) {
    return remaining;
  }
  return Math.min(remaining + owned, MAX_STEPPER_QUANTITY);
};
export const capFscQuantity = (quantity, max) => max > 0 ? Math.min(quantity, max) : quantity;

/*
 * Every seat row for the tier as one uniform list: the primary seat first,
 * flagged `isPrimary` so consumers don't re-derive which one it is, followed by
 * each other seat type. Prices come from the caller's catalog rather than from
 * the tier catalog, whose productPrices map is empty.
 */
export const buildFscSeatTypes = (catalog, selectedTier, pricesByProductApiName, primary, otherQuantities, otherMinQuantities = {}, ownedQuantities = {
  primaryQuantity: 0,
  otherQuantities: {}
}) => {
  var _catalog$products5, _pricesByProductApiNa;
  const pricingData = getFscTierSeatApiNames(catalog, selectedTier);
  if (!pricingData) {
    return [];
  }
  const {
    primaryApiName,
    otherSeatApiNames
  } = pricingData;
  const primaryOwned = ownedQuantities.primaryQuantity;
  const primarySeatType = Object.assign({}, getFscSeatNameAndDescription(catalog, primaryApiName), {
    stepper: {
      quantity: Math.max(primary.quantity, primary.min),
      min: primary.min,
      max: resolveFscAbsoluteMaxQuantity((_catalog$products5 = catalog.products) === null || _catalog$products5 === void 0 ? void 0 : _catalog$products5[primaryApiName], primaryOwned)
    },
    pricePerSeat: (_pricesByProductApiNa = pricesByProductApiName.get(primaryApiName)) !== null && _pricesByProductApiNa !== void 0 ? _pricesByProductApiNa : 0,
    ownedQuantity: primaryOwned,
    isPrimary: true
  });
  const otherSeatTypes = otherSeatApiNames.map(apiName => {
    var _otherMinQuantities$a, _ownedQuantities$othe, _otherQuantities$apiN, _catalog$products6, _pricesByProductApiNa2;
    const min = (_otherMinQuantities$a = otherMinQuantities[apiName]) !== null && _otherMinQuantities$a !== void 0 ? _otherMinQuantities$a : 0;
    const owned = (_ownedQuantities$othe = ownedQuantities.otherQuantities[apiName]) !== null && _ownedQuantities$othe !== void 0 ? _ownedQuantities$othe : 0;
    return Object.assign({}, getFscSeatNameAndDescription(catalog, apiName), {
      stepper: {
        quantity: Math.max((_otherQuantities$apiN = otherQuantities[apiName]) !== null && _otherQuantities$apiN !== void 0 ? _otherQuantities$apiN : 0, min),
        min,
        max: resolveFscAbsoluteMaxQuantity((_catalog$products6 = catalog.products) === null || _catalog$products6 === void 0 ? void 0 : _catalog$products6[apiName], owned)
      },
      pricePerSeat: (_pricesByProductApiNa2 = pricesByProductApiName.get(apiName)) !== null && _pricesByProductApiNa2 !== void 0 ? _pricesByProductApiNa2 : 0,
      ownedQuantity: owned,
      isPrimary: false
    });
  });
  return [primarySeatType, ...otherSeatTypes];
};
const isFscSeatAtLimit = seat => seat.stepper.max !== undefined && seat.stepper.max > 0 && seat.stepper.quantity >= seat.stepper.max;

/* Prefer the primary (GTM) seat so the banner reports the tier-level cap,
 * not the cap of a secondary seat type like OPS that may be lower. */
export const findFscSeatAtLimit = seatTypes => {
  var _seatTypes$find;
  return (_seatTypes$find = seatTypes.find(seat => seat.isPrimary && isFscSeatAtLimit(seat))) !== null && _seatTypes$find !== void 0 ? _seatTypes$find : seatTypes.find(isFscSeatAtLimit);
};

/* Scan forward for the nearest tier that actually exists in the catalog;
 * skips gaps (e.g. STARTER → ENTERPRISE when PROFESSIONAL is absent). */
export const getFscNextTier = (catalog, tier) => {
  const currentIdx = currentOrderedPaidProductLevels.indexOf(tier);
  return currentOrderedPaidProductLevels.slice(currentIdx + 1).find(level => {
    var _catalog$productsByTi2;
    return Boolean((_catalog$productsByTi2 = catalog.productsByTier) === null || _catalog$productsByTi2 === void 0 ? void 0 : _catalog$productsByTi2[level]);
  });
};